// =====================================================================
// cslProcessor — lazy-loaded citeproc-js renderer fed by the Lovable
// Cloud `csl-styles` private bucket via the `csl-style-fetch` edge fn.
//
// Why lazy: citeproc-js is ~250KB gzip and only runs on the Composer
// citations panel + settings preview. Vite splits it into its own
// chunk; first render in a session adds one chunk fetch (~50–150ms),
// every subsequent render is local.
//
// Caching layers:
//   1. IndexedDB (`csl-style-cache` DB, `xml` store) — keyed by
//      `<effectiveStyleId>@<version>` and `locale:<lang>@<version>`.
//      Survives across sessions. First fetch per browser per style.
//   2. In-memory Map of parsed citeproc Engine instances, keyed by
//      `<effectiveStyleId>@<version>:<locale>`. Cleared on page reload.
//
// Public API:
//   renderBibliography(styleId, items, opts?) → string[]   // HTML entries
//   renderCitations(styleId, groups, opts?)   → string[]   // HTML inline citations
//
// `items` are CSL-JSON objects (CslItem from src/lib/citations.ts).
// Engines are stateful (citeproc needs `updateItems` for citation
// numbering / Ibid. tracking) but we build a fresh engine per render
// call to keep callers simple and idempotent. The expensive parse
// (style XML) is amortized by the in-memory style-string cache.
// =====================================================================

import { supabase } from "@/integrations/supabase/client";
import type { CslItem } from "@/lib/citations";

const DB_NAME = "csl-style-cache";
const STORE = "xml";
const DB_VERSION = 1;

type FetchResponse = {
  styleId: string;
  effectiveStyleId: string;
  styleUrl: string;
  localeUrl?: string;
  version: string;
};

// ---------- IndexedDB helpers ----------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key: string): Promise<string | null> {
  try {
    const db = await openDb();
    return await new Promise<string | null>((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as string | undefined) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function idbSet(key: string, value: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* swallow — cache is best-effort */
  }
}

// ---------- Style + locale loading ----------

// In-flight de-dup so concurrent renders of the same style share one fetch.
const inflight = new Map<string, Promise<{ styleXml: string; localeXml: string | null; version: string }>>();

async function loadStyleAndLocale(
  styleId: string,
  locale: string,
): Promise<{ styleXml: string; localeXml: string | null; version: string }> {
  const inflightKey = `${styleId}:${locale}`;
  const pending = inflight.get(inflightKey);
  if (pending) return pending;

  const job = (async () => {
    const { data, error } = await supabase.functions.invoke<FetchResponse>(
      "csl-style-fetch",
      { body: { styleId, locale } },
    );
    if (error || !data) {
      throw new Error(`csl-style-fetch failed: ${error?.message ?? "unknown"}`);
    }

    const styleCacheKey = `${data.effectiveStyleId}@${data.version}`;
    let styleXml = await idbGet(styleCacheKey);
    if (!styleXml) {
      const res = await fetch(data.styleUrl);
      if (!res.ok) throw new Error(`Failed to fetch style XML (${res.status})`);
      styleXml = await res.text();
      void idbSet(styleCacheKey, styleXml);
    }

    let localeXml: string | null = null;
    if (data.localeUrl) {
      const localeCacheKey = `locale:${locale}@${data.version}`;
      localeXml = await idbGet(localeCacheKey);
      if (!localeXml) {
        const res = await fetch(data.localeUrl);
        if (res.ok) {
          localeXml = await res.text();
          void idbSet(localeCacheKey, localeXml);
        }
      }
    }

    return { styleXml, localeXml, version: data.version };
  })();

  inflight.set(inflightKey, job);
  try {
    return await job;
  } finally {
    inflight.delete(inflightKey);
  }
}

// ---------- Engine construction ----------

// `citeproc` has no first-party types; treat it as unknown and cast at boundaries.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CslEngine = any;

let citeprocModulePromise: Promise<{ Engine: new (sys: unknown, style: string, lang?: string) => CslEngine }> | null = null;

async function loadCiteproc() {
  if (!citeprocModulePromise) {
    citeprocModulePromise = (async () => {
      const mod = await import("citeproc");
      // citeproc exposes `CSL` as default or namespace export depending on bundler.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const CSL = (mod as any).default ?? (mod as any).CSL ?? mod;
      return { Engine: CSL.Engine };
    })();
  }
  return citeprocModulePromise;
}

// FALLBACK_LOCALE_XML keeps citeproc from throwing when our bucket is
// missing the requested locale (or it hasn't synced yet). A minimal
// en-US shell is enough for English-language renders.
const FALLBACK_LOCALE_XML = `<?xml version="1.0" encoding="utf-8"?>
<locale xmlns="http://purl.org/net/xbiblio/csl" version="1.0" xml:lang="en-US">
<info><translator><name>fallback</name></translator><rights license="http://creativecommons.org/licenses/by-sa/3.0/">CC BY-SA 3.0</rights><updated>2024-01-01T00:00:00+00:00</updated></info>
<style-options punctuation-in-quote="true"/>
<date form="text"><date-part name="month" suffix=" "/><date-part name="day" suffix=", "/><date-part name="year"/></date>
<date form="numeric"><date-part name="month" form="numeric-leading-zeros" suffix="/"/><date-part name="day" form="numeric-leading-zeros" suffix="/"/><date-part name="year"/></date>
<terms></terms>
</locale>`;

type EngineEntry = { engine: CslEngine; itemMap: Map<string, CslItem> };
const engineCache = new Map<string, EngineEntry>();

async function getEngine(
  styleId: string,
  locale: string,
): Promise<EngineEntry> {
  const cacheKey = `${styleId}@${locale}`;
  const hit = engineCache.get(cacheKey);
  if (hit) return hit;

  const [{ Engine }, { styleXml, localeXml }] = await Promise.all([
    loadCiteproc(),
    loadStyleAndLocale(styleId, locale),
  ]);

  const itemMap = new Map<string, CslItem>();
  const sys = {
    retrieveLocale: () => localeXml ?? FALLBACK_LOCALE_XML,
    retrieveItem: (id: string | number) => {
      const item = itemMap.get(String(id));
      if (!item) throw new Error(`citeproc: unknown item id ${id}`);
      return item;
    },
  };

  const engine = new Engine(sys, styleXml, locale);
  const entry: EngineEntry = { engine, itemMap };
  engineCache.set(cacheKey, entry);
  return entry;
}

// ---------- Public API ----------

export type RenderOpts = {
  /** BCP47 locale; defaults to "en-US". */
  locale?: string;
};

/**
 * Render a bibliography for the given CSL items. Returns one HTML entry
 * per item, in the order citeproc emits them (which may differ from
 * input order for styles that sort by author / year). Item.id is the
 * stable join key — caller is responsible for mapping rendered entries
 * back to their input items if needed.
 */
export async function renderBibliography(
  styleId: string,
  items: CslItem[],
  opts: RenderOpts = {},
): Promise<{ ids: string[]; entries: string[] }> {
  const locale = opts.locale ?? "en-US";
  const { engine, itemMap } = await getEngine(styleId, locale);
  itemMap.clear();
  const ids: string[] = [];
  for (const it of items) {
    const id = String(it.id ?? "");
    if (!id) continue;
    itemMap.set(id, it);
    ids.push(id);
  }
  engine.updateItems(ids);
  const [meta, rendered] = engine.makeBibliography() as [
    { entry_ids: Array<[string]> },
    string[],
  ];
  const orderedIds = meta.entry_ids.map(([id]) => id);
  return { ids: orderedIds, entries: rendered };
}

export type CitationCluster = {
  items: Array<{ id: string; locator?: string }>;
  /** "note" emits superscript-style numbered notes; "in-text" emits parentheticals. */
  mode?: "note" | "in-text";
};

/**
 * Render inline citation clusters. Each input cluster yields one HTML
 * string. Engine state is reset before each call so callers don't have
 * to worry about leakage between Pieces.
 */
export async function renderCitations(
  styleId: string,
  itemsById: Record<string, CslItem>,
  clusters: CitationCluster[],
  opts: RenderOpts = {},
): Promise<string[]> {
  const locale = opts.locale ?? "en-US";
  const { engine, itemMap } = await getEngine(styleId, locale);
  itemMap.clear();
  for (const [id, item] of Object.entries(itemsById)) itemMap.set(id, item);
  engine.updateItems(Object.keys(itemsById));

  const out: string[] = [];
  for (let i = 0; i < clusters.length; i++) {
    const c = clusters[i];
    const citation = {
      citationItems: c.items.map((it) => ({
        id: it.id,
        ...(it.locator ? { locator: it.locator } : {}),
      })),
      properties: { noteIndex: c.mode === "in-text" ? 0 : i + 1 },
    };
    // processCitationCluster returns [info, [[noteIndex, html, citationId], ...]]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = engine.processCitationCluster(citation, [], []) as [unknown, any[]];
    const last = result[1][result[1].length - 1];
    out.push(last ? String(last[1]) : "");
  }
  return out;
}

/**
 * Returns the CSL style's top-level `class` attribute, which decides
 * whether citations render as numbered/foot notes or inline parentheticals.
 * Reads from the same cached XML the engine uses, so this only ever
 * triggers a network round-trip once per browser per style.
 */
const styleClassCache = new Map<string, "note" | "in-text">();
export async function getStyleClass(
  styleId: string,
  locale = "en-US",
): Promise<"note" | "in-text"> {
  const cacheKey = `${styleId}@${locale}`;
  const hit = styleClassCache.get(cacheKey);
  if (hit) return hit;
  const { styleXml } = await loadStyleAndLocale(styleId, locale);
  // Match the root <style ... class="note|in-text"> attribute. CSL spec
  // guarantees the attribute lives on the root element; default is "in-text".
  const m = /<style\b[^>]*\bclass="(note|in-text)"/i.exec(styleXml);
  const cls: "note" | "in-text" = m?.[1] === "note" ? "note" : "in-text";
  styleClassCache.set(cacheKey, cls);
  return cls;
}

/** Test-only: clear all caches. Not exported in the index barrel. */
export function __resetCslProcessorCaches() {
  engineCache.clear();
  inflight.clear();
  styleClassCache.clear();
  citeprocModulePromise = null;
}
